import { createSignal } from 'solid-js'

const SUPPORTED_LOCALES = ['en', 'de', 'es', 'fr'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

const DEFAULT_LOCALE: Locale = 'en'

export type TranslationParams = Record<string, string | number>
export type TranslationValue = string | ((params?: TranslationParams) => string)
export type TranslationDictionary = Record<string, TranslationValue>

/**
 * Reads the numeric `count` parameter from translation params, defaulting to 0.
 *
 * @param params - The translation params.
 * @returns The count value.
 */
function getCountParam(params?: TranslationParams) {
   const count = params?.count
   return typeof count === 'number' ? count : 0
}

const EN_TRANSLATIONS: TranslationDictionary = {
   /**
    * Formats the fast-count badge label with singular/plural handling.
    *
    * @param params - Translation params containing `count`.
    * @returns The pluralized label.
    */
   'fast count badge': params => {
      const count = getCountParam(params)
      return `${count} fast${count === 1 ? '' : 's'}`
   },
   ' / {value} g': ' / {value} g',
   ' g': ' g',
   'In progress': 'In progress',
   'Modeled for a healthy adult, not measured':
      'Modeled for a healthy adult, not measured',
   'Use arrow keys to inspect the timeline': 'Use arrow keys to inspect the timeline',
   'Blood glucose': 'Blood glucose',
   'Insulin': 'Insulin',
   'Liver glycogen': 'Liver glycogen',
   'Ketones': 'Ketones',
   'Fat burned': 'Fat burned',
   'Fat burned:': 'Fat burned:',
   'hours': 'hours',
   'Progress percentage label': '{percentage}% of {target}',
   'Showing {start}-{end} of {total}': 'Showing {start}-{end} of {total}',
   'Prev': 'Prev',
   'Next': 'Next',
   'Weight': 'Weight',
   'Measurements': 'Measurements',
   'Run': 'Run',
   'Swim': 'Swim',
   'Cycle': 'Cycle',
   'Row': 'Row',
   'Sleep': 'Sleep',
   'Start Sleep': 'Start Sleep',
   'End Sleep': 'End Sleep',
   'Last sleep': 'Last sleep',
   'Wake-up target': 'Wake-up target',
   'Woke up': 'Woke up',
   'Sleep per night': 'Sleep per night',
   'Average sleep per {bucket}': 'Average sleep per {bucket}',
   'night': 'night',
   '{hours}h {minutes}m': '{hours}h {minutes}m',
   'Edit sleep': 'Edit sleep',
   'Fell asleep': 'Fell asleep',
   'Woke up (leave empty while still asleep)': 'Woke up (leave empty while still asleep)',
   'Enter when the sleep started.': 'Enter when the sleep started.',
   'Enter when you woke up.': 'Enter when you woke up.',
   'Waking up must come after falling asleep.':
      'Waking up must come after falling asleep.',
   'Another sleep is already being timed.': 'Another sleep is already being timed.',
   'Add metric': 'Add metric',
   'Log {metric}': 'Log {metric}',
   'Distance ({unit})': 'Distance ({unit})',
   'Time (m:ss)': 'Time (m:ss)',
   'Distance': 'Distance',
   'All': 'All',
   'Latest': 'Latest',
   'pace': 'pace',
   'speed': 'speed',
   'Click to switch between pace and speed.': 'Click to switch between pace and speed.',
   'day': 'day',
   'week': 'week',
   'month': 'month',
   'Best {value}': 'Best {value}',
   'Best {distance} {value}': 'Best {distance} {value}',
   '{value} over time': '{value} over time',
   'Distance per {bucket}': 'Distance per {bucket}',
   'Effort per {bucket}': 'Effort per {bucket}',
   'Target {value} per {bucket}': 'Target {value} per {bucket}',
   '{value} to target': '{value} to target',
   'Target reached (+{value})': 'Target reached (+{value})',
   '{value} effort': '{value} effort',
   'No {metric} entries yet.': 'No {metric} entries yet.',
   'No entries in this range yet.': 'No entries in this range yet.',
   'Edit entry': 'Edit entry',
   'Enter a distance and a duration.': 'Enter a distance and a duration.',
   'Enter values above 0.': 'Enter values above 0.',
   'Entries cannot be in the future.': 'Entries cannot be in the future.',
   'Entry deleted.': 'Entry deleted.',
   'Entry updated.': 'Entry updated.',
   'Logged {metric}.': 'Logged {metric}.',
   'Selected entry is no longer available.': 'Selected entry is no longer available.',
   'Edit Weight': 'Edit Weight',
   'Weight ({unit})': 'Weight ({unit})',
   'Date and time': 'Date and time',
   'Enter a valid date and time.': 'Enter a valid date and time.',
   'Weight entries cannot be in the future.': 'Weight entries cannot be in the future.',
   'Another entry already exists on that day.':
      'Another entry already exists on that day.',
   'Enter a valid weight above 0.': 'Enter a valid weight above 0.',
   'Log Current Weight ({unit})': 'Log Current Weight ({unit})',
   'e.g., {value}': 'e.g., {value}',
   'Units': 'Units',
   'Metric (kg, cm, km)': 'Metric (kg, cm, km)',
   'Imperial (lb, in, mi)': 'Imperial (lb, in, mi)',
   'Applies to how measurements are shown and entered on every device.':
      'Applies to how measurements are shown and entered on every device.',
   'Height ({unit})': 'Height ({unit})',
   'Target Weight ({unit})': 'Target Weight ({unit})',
   'Height must be between {min} and {max}.': 'Height must be between {min} and {max}.',
   'Target weight must be between {min} and {max}.':
      'Target weight must be between {min} and {max}.',
   'Monthly run effort target': 'Monthly run effort target',
   'Effort = distance (km) × a multiplier that doubles every 4 km/h: 1× at 6 km/h, 2× at 10, 4× at 14 (0 below 6 km/h). 2 km at 10 km/h scores 4. The target shows as a dashed line on the run effort chart.':
      'Effort = distance (km) × a multiplier that doubles every 4 km/h: 1× at 6 km/h, 2× at 10, 4× at 14 (0 below 6 km/h). 2 km at 10 km/h scores 4. The target shows as a dashed line on the run effort chart.',
   'Monthly run effort target must be a positive number up to {max}.':
      'Monthly run effort target must be a positive number up to {max}.',
   'Habits': 'Habits',
   'Habit history': 'Habit history',
   'Edit habit': 'Edit habit',
   'Past 30 days': 'Past 30 days',
   'Habit completion history': 'Habit completion history',
   'All done': 'All done',
   'Some done': 'Some done',
   'Missed or pending': 'Missed or pending',
   'Add habit': 'Add habit',
   'Habit name': 'Habit name',
   'Add a habit': 'Add a habit',
   'Task type': 'Task type',
   'Repeating': 'Repeating',
   'One-off': 'One-off',
   'Due date': 'Due date',
   'Available on due date': 'Available on due date',
   'Repeat after days': 'Repeat after days',
   'Calendar': 'Calendar',
   'First date': 'First date',
   'Time': 'Time',
   'Repeats': 'Repeats',
   'Every day': 'Every day',
   'Every weekday': 'Every weekday',
   'Every week': 'Every week',
   'Every 2 weeks': 'Every 2 weeks',
   'Every month': 'Every month',
   'Every year': 'Every year',
   'Custom': 'Custom',
   'Custom rule (RRULE)': 'Custom rule (RRULE)',
   'Custom schedule': 'Custom schedule',
   'No upcoming dates': 'No upcoming dates',
   'Add': 'Add',
   'No habits yet': 'No habits yet',
   'Every': 'Every',
   'Every {count} days': 'Every {count} days',
   'days': 'days',
   'Done': 'Done',
   'Pause': 'Pause',
   'Paused': 'Paused',
   'Resume': 'Resume',
   'Tags': 'Tags',
   'Tagged tasks can be paused and resumed together.':
      'Tagged tasks can be paused and resumed together.',
   'Add a tag': 'Add a tag',
   'Type to create a tag': 'Type to create a tag',
   'Create tag "{name}"': 'Create tag "{name}"',
   'Pause by tag': 'Pause by tag',
   'Add tags to tasks to pause and resume them together.':
      'Add tags to tasks to pause and resume them together.',
   '{paused} of {count} paused': '{paused} of {count} paused',
   /**
    * Formats a tag's task count with singular/plural handling.
    *
    * @param params - Translation params containing `count`.
    * @returns The pluralized label.
    */
   'tag task count': params => {
      const count = getCountParam(params)
      return `${count} ${count === 1 ? 'task' : 'tasks'}`
   },
   'Delete': 'Delete',
   'Due now': 'Due now',
   'Due tomorrow': 'Due tomorrow',
   'Due at {time}': 'Due at {time}',
   'Due in {count} days': 'Due in {count} days',
   'Not completed yet': 'Not completed yet',
   'Completed less than 1 hour ago': 'Completed less than 1 hour ago',
   'Completed 1 hour ago': 'Completed 1 hour ago',
   'Completed {count} hours ago': 'Completed {count} hours ago',
   'Completed yesterday': 'Completed yesterday',
   'Completed {count} days ago': 'Completed {count} days ago',
   '{count} habits due': '{count} habits due',
   'All habits are on track': 'All habits are on track',
   '{count} complete days': '{count} complete days',
   'Account': 'Account',
   'Download your fasting, weight, and account data as a JSON file.':
      'Download your fasting, weight, and account data as a JSON file.',
   'Download My Data': 'Download My Data',
   'Cloud Sync': 'Cloud Sync',
   'Use an existing synchronization key': 'Use an existing synchronization key',
   'Enter the synchronization key from another device to load the cloud data connected to it on this device.':
      'Enter the synchronization key from another device to load the cloud data connected to it on this device.',
   'Synchronization Key': 'Synchronization Key',
   'Paste the synchronization key': 'Paste the synchronization key',
   'If you already have local data here, it will be replaced with the synced data tied to this key.':
      'If you already have local data here, it will be replaced with the synced data tied to this key.',
   'Enter a valid synchronization key.': 'Enter a valid synchronization key.',
   'Load sync key': 'Load sync key',
   'Preparing your data export…': 'Preparing your data export…',
   'Data export ready. Check your downloads.': 'Data export ready. Check your downloads.',
   'Data export failed. Please try again.': 'Data export failed. Please try again.',
   'Toggle timer display mode': 'Toggle timer display mode',
   'Loading…': 'Loading…',
   'Loading latest cloud state…': 'Loading latest cloud state…',
   'Start a fast to ignite those 🔥 milestones!':
      'Start a fast to ignite those 🔥 milestones!',
   'Could not start fast because cloud sync is still catching up. Please try again.':
      'Could not start fast because cloud sync is still catching up. Please try again.',
   'A fast is already active in cloud sync. Loaded the latest fast from your account.':
      'A fast is already active in cloud sync. Loaded the latest fast from your account.',
   'Could not start fast because cloud data changed. Review the latest synced state and try again.':
      'Could not start fast because cloud data changed. Review the latest synced state and try again.',
   'Update': 'Update',
   'Editing entry from {timestamp}.': 'Editing entry from {timestamp}.',
   'Deleted entry from {timestamp}.': 'Deleted entry from {timestamp}.',
   'Sync key active.': 'Sync key active.',
   ' (attempt {count})': ' (attempt {count})',
   " We'll retry automatically in {seconds} {unit}.":
      " We'll retry automatically in {seconds} {unit}.",
   " We'll retry automatically shortly.": " We'll retry automatically shortly.",
   'Sync key active, but the latest cloud update failed{attemptLabel}.':
      'Sync key active, but the latest cloud update failed{attemptLabel}.',
   'Height saved: {height}.': 'Height saved: {height}.',
   'Add your height to unlock BMI insights.': 'Add your height to unlock BMI insights.',
   'Target weight set to {weight}.': 'Target weight set to {weight}.',
   'Set a target weight to track your goal in the charts.':
      'Set a target weight to track your goal in the charts.',
   'Monthly run effort target: {value}.': 'Monthly run effort target: {value}.',
   'Combined fat burned across your fasts this month.':
      'Combined fat burned across your fasts this month.',
   'Fast ends {dayLabel} at {time}': 'Fast ends {dayLabel} at {time}',
   'Fast ended {dayLabel} at {time}': 'Fast ended {dayLabel} at {time}',
   'today': 'today',
   'tomorrow': 'tomorrow',
   'in {count} days': 'in {count} days',
   'yesterday': 'yesterday',
   '{count} days ago': '{count} days ago',
   'Stay strong! 🔥 Milestones will light up as you progress.':
      'Stay strong! 🔥 Milestones will light up as you progress.',
   'First bar on the way! 🍫 = 50 g': 'First bar on the way! 🍫 = 50 g',
   'Trend projection pending...': 'Trend projection pending...',
   'Set a target weight to see a projected timeline.':
      'Set a target weight to see a projected timeline.',
   'Add weight entries to build a trend projection.':
      'Add weight entries to build a trend projection.',
   'Add entries on more days to build a trend projection.':
      'Add entries on more days to build a trend projection.',
   'You are already at your target weight—great job!':
      'You are already at your target weight—great job!',
   'Recent trend is flat, so projection is unavailable.':
      'Recent trend is flat, so projection is unavailable.',
   'Projected to reach your target {duration}.':
      'Projected to reach your target {duration}.',
   'Projected to reach your target on {date} ({duration}).':
      'Projected to reach your target on {date} ({duration}).',
   'Trend suggests you already passed your target {duration}.':
      'Trend suggests you already passed your target {duration}.',
   'Trend suggests you already passed your target on {date} ({duration}).':
      'Trend suggests you already passed your target on {date} ({duration}).',
   'Current trend suggests you are distancing from your target weight.':
      'Current trend suggests you are distancing from your target weight.',
   'Projected to reach your target today.': 'Projected to reach your target today.',
   'Normal BMI expected on {date} ({duration}).':
      'Normal BMI expected on {date} ({duration}).',
   'Normal BMI expected {duration}.': 'Normal BMI expected {duration}.',
   'Current trend is moving away from your target.':
      'Current trend is moving away from your target.',
   'Trend projection unavailable.': 'Trend projection unavailable.',
   'Click to toggle weight chart view.': 'Click to toggle weight chart view.',
   'Scatter with trend line': 'Scatter with trend line',
   'Line chart': 'Line chart',
   'Weight history chart - {mode}. {instruction}':
      'Weight history chart - {mode}. {instruction}',
   'Week': 'Week',
   'Month': 'Month',
   '3 Months': '3 Months',
   'Year': 'Year',
   'Max': 'Max',
}

const translationStore: Partial<Record<Locale, TranslationDictionary>> = {
   en: EN_TRANSLATIONS,
}

const translationLoaders: Record<Locale, (() => Promise<TranslationDictionary>) | null> =
   {
      en: null,
      /**
       * Lazily imports the German translation dictionary.
       *
       * @returns The loaded dictionary.
       */
      de: () => import('./locales/de').then(module => module.default),
      /**
       * Lazily imports the Spanish translation dictionary.
       *
       * @returns The loaded dictionary.
       */
      es: () => import('./locales/es').then(module => module.default),
      /**
       * Lazily imports the French translation dictionary.
       *
       * @returns The loaded dictionary.
       */
      fr: () => import('./locales/fr').then(module => module.default),
   }

const translationLoadPromises: Partial<Record<Locale, Promise<void>>> = {}

const [translationsRevision, setTranslationsRevision] = createSignal(0)

/**
 * Ensures a locale's translations are loaded, coalescing concurrent loads.
 *
 * @param locale - The locale to load.
 * @returns A promise that resolves once the dictionary is available.
 */
function ensureLocale(locale: Locale): Promise<void> {
   if (translationStore[locale]) {
      return Promise.resolve()
   }

   const pending = translationLoadPromises[locale]
   if (pending) {
      return pending
   }

   const loader = translationLoaders[locale]
   if (!loader) {
      return Promise.resolve()
   }

   const loadPromise = loader()
      .then(dictionary => {
         translationStore[locale] = dictionary
         setTranslationsRevision(prev => prev + 1)
      })
      .catch(error => {
         console.error(`[i18n] Failed to load translations for "${locale}"`, error)
      })
      .finally(() => {
         delete translationLoadPromises[locale]
      })

   translationLoadPromises[locale] = loadPromise
   return loadPromise
}

/**
 * Normalizes a value to a supported locale code, or null.
 *
 * @param value - The candidate locale string.
 * @returns The supported locale, or null.
 */
function normalizeLocale(value: string | null | undefined): Locale | null {
   if (typeof value !== 'string') {
      return null
   }
   const lower = value.toLowerCase()
   return SUPPORTED_LOCALES.find(locale => locale === lower) ?? null
}

/**
 * Normalizes a value to a supported locale code (public wrapper).
 *
 * @param value - The candidate locale string.
 * @returns The supported locale, or null.
 */
export function normalizeLocaleCode(value: string | null | undefined): Locale | null {
   return normalizeLocale(value)
}

/**
 * Detects the best supported locale from the browser's language preferences.
 *
 * @returns The detected locale, or the default.
 */
function detectNavigatorLocale(): Locale {
   if (typeof navigator === 'undefined') {
      return DEFAULT_LOCALE
   }

   const { languages, language } = navigator
   const preferred =
      Array.isArray(languages) && languages.length > 0 ? languages : [language]

   for (const candidate of preferred) {
      if (typeof candidate !== 'string') {
         continue
      }
      const normalized = normalizeLocale(candidate)
      if (normalized) {
         return normalized
      }
      const lower = candidate.toLowerCase()
      const match = SUPPORTED_LOCALES.find(
         locale => lower === locale || lower.startsWith(`${locale}-`),
      )
      if (match) {
         return match
      }
   }

   return DEFAULT_LOCALE
}

let localeOverride: Locale | null = null

const [localeSignal, setLocaleSignal] = createSignal<Locale>(detectNavigatorLocale())

void ensureLocale(localeSignal())

/**
 * Applies a locale override (or navigator detection) and loads its translations.
 *
 * @param nextOverride - The locale to force, or null to auto-detect.
 * @returns A promise that resolves once translations are loaded.
 */
function applyLocale(nextOverride: Locale | null): Promise<void> {
   const nextLocale = nextOverride ?? detectNavigatorLocale()
   setLocaleSignal(nextLocale)
   return ensureLocale(nextLocale)
}

/**
 * Sets an explicit locale override and applies it.
 *
 * @param next - The locale to force, or null to clear the override.
 * @returns A promise that resolves once translations are loaded.
 */
export function setLocaleOverride(next: Locale | null): Promise<void> {
   localeOverride = next
   return applyLocale(localeOverride)
}

/**
 * Returns the currently active locale.
 *
 * @returns The active locale.
 */
export function getLocale(): Locale {
   return localeSignal()
}

/**
 * Re-detects the locale from the navigator unless an override is set.
 */
export function refreshLocaleFromNavigator() {
   if (localeOverride) {
      return
   }
   applyLocale(null)
}

/**
 * Interpolates `{name}` placeholders in a template with param values.
 *
 * @param template - The template string.
 * @param params - The interpolation values.
 * @returns The formatted string.
 */
function format(template: string, params: TranslationParams = {}): string {
   return template.replace(/\{(\w+)\}/g, (_, key: string) => {
      const value = params[key]
      return value === undefined || value === null ? '' : String(value)
   })
}

/**
 * Translates a key for the active locale, interpolating params and falling back to English or the key.
 *
 * @param key - The translation key.
 * @param params - Optional interpolation values.
 * @returns The translated, interpolated string.
 */
export function translate(key: string, params?: TranslationParams): string {
   translationsRevision()

   const activeLocale = localeSignal()

   if (!translationStore[activeLocale]) {
      void ensureLocale(activeLocale)
   }

   const localeMap = translationStore[activeLocale] ?? EN_TRANSLATIONS
   const fallbackMap = translationStore[DEFAULT_LOCALE] ?? EN_TRANSLATIONS

   const candidate = localeMap[key] ?? fallbackMap[key]

   if (typeof candidate === 'function') {
      return candidate(params)
   }

   if (typeof candidate === 'string') {
      return format(candidate, params)
   }

   return format(key, params)
}

/**
 * Returns a `translate` function bound for use in components.
 *
 * @returns A translate function.
 */
export function useI18n() {
   return (key: string, params?: TranslationParams) => translate(key, params)
}
